import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../api';
import { loadStoredAuth } from '../authStorage';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { EmptyState } from '../components/ui/EmptyState';
import { ErrorState } from '../components/ui/ErrorState';
import { Skeleton } from '../components/ui/Skeleton';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'job', label: 'Jobs' },
  { id: 'customer', label: 'Customers' },
  { id: 'invoice', label: 'Invoices' },
  { id: 'login', label: 'Auth' },
  { id: 'account', label: 'Accounts' },
];

const iconForAction = (action) => {
  const normalized = String(action || '').toLowerCase();
  if (normalized.includes('login')) return 'LOG';
  if (normalized.includes('logout')) return 'OUT';
  if (normalized.includes('created')) return 'ADD';
  if (normalized.includes('updated')) return 'EDT';
  if (normalized.includes('deleted')) return 'DEL';
  if (normalized.includes('checkin')) return 'IN';
  if (normalized.includes('checkout')) return 'DONE';
  if (normalized.includes('invoice')) return 'INV';
  if (normalized.includes('account_status')) return 'ACC';
  if (normalized.includes('status')) return 'STS';
  return 'ACT';
};

const RefreshIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
    <path d="M11.5 6.5A5 5 0 1 1 9 2.1" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M9 1v3h3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

function ActivityStat({ label, value, tone = 'default' }) {
  return (
    <article className={`activity-stat activity-stat--${tone}`}>
      <span className="activity-stat-label">{label}</span>
      <strong className="activity-stat-value">{value}</strong>
    </article>
  );
}

export function ActivityPage() {
  const navigate = useNavigate();
  const auth = loadStoredAuth();
  const token = auth?.token;

  const [activities, setActivities] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!token) navigate('/login');
  }, [token, navigate]);

  const loadActivity = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch('/api/activity', { token });
      setActivities(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Failed to load activity');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadActivity();
  }, [loadActivity, refreshKey]);

  const filteredActivities = useMemo(() => {
    if (filter === 'all') return activities;
    if (filter === 'account') {
      return activities.filter((entry) =>
        String(entry.action || '').includes('account_status') ||
        String(entry.entityType || entry.entity_type || '').toLowerCase() === 'user'
      );
    }
    return activities.filter((entry) => String(entry.action || '').includes(filter));
  }, [activities, filter]);

  const stats = useMemo(() => ({
    total: activities.length,
    auth: activities.filter((entry) => String(entry.action || '').includes('login')).length,
    account: activities.filter((entry) => String(entry.action || '').includes('account_status')).length,
    recent: activities.slice(0, 10).length,
  }), [activities]);

  if (loading) {
    return (
      <section className="activity-page">
        <Skeleton lines={10} />
      </section>
    );
  }

  return (
    <section className="activity-page">
      <div className="activity-header">
        <div>
          <span className="activity-eyebrow">Audit</span>
          <h1 className="activity-heading">Activity</h1>
          <p className="activity-subheading">
            Track operational changes, authentication events, and cross-system updates in one feed.
          </p>
        </div>
        <Button variant="secondary" onClick={() => setRefreshKey((value) => value + 1)}>
          <RefreshIcon />
          Refresh
        </Button>
      </div>

      {error ? <ErrorState message={error} onRetry={loadActivity} /> : null}

      <div className="activity-stats-grid">
        <ActivityStat label="Total Events" value={stats.total} />
        <ActivityStat label="Auth Events" value={stats.auth} tone="accent" />
        <ActivityStat label="Account Changes" value={stats.account} tone="warning" />
        <ActivityStat label="Visible Feed" value={stats.recent} tone="info" />
      </div>

      <Card title="Filters">
        <div className="activity-filter-row">
          {FILTERS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={`activity-filter-btn${filter === option.id ? ' is-active' : ''}`}
              onClick={() => setFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </Card>

      <Card title="Feed">
        {filteredActivities.length ? (
          <div className="activity-feed">
            {filteredActivities.map((entry) => (
              <div key={entry.id} className="activity-entry">
                <span className="activity-icon">{iconForAction(entry.action)}</span>
                <div className="activity-content">
                  <p className="activity-description">{entry.description}</p>
                  <span className="activity-meta">
                    {entry.userId || entry.user_id || 'system'} · {new Date(entry.timestamp).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <EmptyState title="No activity for this filter" message="Try a broader filter or refresh the feed." />
        )}
      </Card>
    </section>
  );
}
